import {
  useCidLabel,
  useCidSearch,
} from '@/features/quotes/hooks/group-life/use-cid-search.hook';
import { useDebouncedValue } from '@/shared/utils';
import { Combobox, Field, Spinner } from '@noorden-insurtech/alba-design-system';
import { useEffect, useMemo, useState } from 'react';

type Props = {
  /** Código do CID selecionado (ex.: `A00.1`). */
  value: string;
  onChange: (code: string) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: string;
};

/**
 * Autocomplete de CID-10 — busca SERVER-SIDE (Public-Data API) por descrição
 * ou código conforme o corretor digita. Espelha o `CnaeCombobox`, mas o
 * catálogo é grande demais para carregar inteiro, então filtra na API.
 *
 * O valor persistido é o CÓDIGO (`A00.1`); exibimos o `locator`
 * (`A00.1 - descrição`) quando ele está nos resultados, senão o próprio
 * código (caso de edição, em que só temos o código salvo).
 */
export function CidCombobox({
  value,
  onChange,
  placeholder = 'Buscar CID por código ou descrição…',
  disabled = false,
  error,
}: Props) {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search.trim(), 350);
  const { data: items, isFetching } = useCidSearch(debouncedSearch);

  const options = useMemo(
    () =>
      (items ?? []).map(c => ({
        value: c.code,
        label: c.locator || `${c.code} - ${c.description}`,
      })),
    [items],
  );

  // Cache de `código → "A00.1 - descrição"` que persiste entre buscas. Sem
  // ele, ao selecionar um CID a busca muda e o item sai de `options`, fazendo
  // o trigger exibir só o código. Acumulamos de cada resultado que passa.
  const [labelByCode, setLabelByCode] = useState<Record<string, string>>({});
  useEffect(() => {
    if (!options.length) return;
    setLabelByCode(prev => {
      let changed = false;
      const next = { ...prev };
      for (const o of options) {
        if (next[o.value] !== o.label) {
          next[o.value] = o.label;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [options]);

  // Edição: se há um código salvo mas ainda não temos o label dele (nunca
  // passou por uma busca), resolve a descrição pelo próprio código.
  const cachedLabel = value ? labelByCode[value] : undefined;
  const { data: resolvedCid } = useCidLabel(
    value && !cachedLabel ? value : undefined,
  );
  const resolvedLabel =
    resolvedCid?.locator ||
    (resolvedCid ? `${resolvedCid.code} - ${resolvedCid.description}` : '');

  // Persiste o label resolvido no cache (evita re-resolver e mantém após troca
  // de busca).
  useEffect(() => {
    if (value && resolvedLabel && labelByCode[value] !== resolvedLabel) {
      setLabelByCode(prev => ({ ...prev, [value]: resolvedLabel }));
    }
  }, [value, resolvedLabel, labelByCode]);

  // Label do valor selecionado: cache acumulado → resolvido por código → o
  // próprio código (enquanto a descrição não chega).
  const selectedLabel = value
    ? (cachedLabel ?? (resolvedLabel || value))
    : null;

  return (
    <Field.Root invalid={!!error} disabled={disabled}>
      <Combobox.Root
        items={options}
        value={value ? [value] : []}
        onValueChange={details => onChange(details.value[0] || '')}
        onInputValueChange={details => setSearch(details.inputValue)}
        selectionBehavior='replace'
        disabled={disabled}
      >
        <Combobox.Control>
          <Combobox.Trigger>{selectedLabel ?? placeholder}</Combobox.Trigger>
        </Combobox.Control>
        <Combobox.Positioner>
          <Combobox.Content>
            <Combobox.Input
              placeholder='Ex.: colera ou A00.1'
              autoFocus
            />
            {isFetching ? (
              <div className='py-8 flex items-center justify-center'>
                <Spinner size='sm' colorScheme='blue' />
              </div>
            ) : (
              <Combobox.List>
                {debouncedSearch.length === 1 ? (
                  <div className='py-4 text-center text-sm text-alba-gray-500'>
                    Digite ao menos 2 caracteres para buscar
                  </div>
                ) : options.length === 0 ? (
                  <div className='py-4 text-center text-sm text-alba-gray-500'>
                    Nenhum CID encontrado
                  </div>
                ) : (
                  <Combobox.ItemGroup>
                    <Combobox.ItemGroupLabel>CIDs (CID-10)</Combobox.ItemGroupLabel>
                    {options.map(item => (
                      <Combobox.Item key={item.value} item={item}>
                        <Combobox.ItemText>{item.label}</Combobox.ItemText>
                        <Combobox.ItemIndicator />
                      </Combobox.Item>
                    ))}
                  </Combobox.ItemGroup>
                )}
              </Combobox.List>
            )}
          </Combobox.Content>
        </Combobox.Positioner>
      </Combobox.Root>
      {error && <Field.ErrorText>{error}</Field.ErrorText>}
    </Field.Root>
  );
}
